import { useMutation, useQueryClient, useInfiniteQuery, useQuery } from "react-query";
import { apiGet, apiPut } from "@shared/queryHooks/api";
import queryString from "query-string";

/* API Functions
--===================================================-- */

const getInfiniteFreeJobs = async ({ page }) => {
  const params = `?${queryString.stringify({ page })}`;
  return await apiGet({ path: `/admin/organizations/free_jobs_by_published_date${params}` });
};

const getOrgJobs = async (organizationId: number) => {
  const params = `?${queryString.stringify({ organization_id: organizationId })}`;
  window.logger(
    "%c[useAdminJobs] getOrgJobs called",
    "color: #00FFFF",
    { organizationId, params, fullPath: `/admin/jobs${params}` },
  );
  try {
    const result = await apiGet({ path: `/admin/jobs${params}` });
    window.logger(
      "%c[useAdminJobs] getOrgJobs success",
      "color: #00FF00",
      { organizationId, result, itemsCount: result?.items?.length },
    );
    return result;
  } catch (error) {
    window.logger(
      "%c[useAdminJobs] getOrgJobs error",
      "color: #FF0000",
      { organizationId, error },
    );
    throw error;
  }
};

const updateAdminJob = async ({ id, ...job }: { id: number; status?: string; internalAdminStatus?: string; visible?: boolean; archivedAt?: string }) => {
  window.logger(
    "%c[ADMIN][useAdminJobs] updateAdminJob\n\n\n\n",
    "background-color: #FF76D2",
    { id, job },
  );
  return await apiPut({
    path: `/admin/jobs/${id}/admin_update`,
    variables: { job },
  });
};

/* Hooks
--===================================================-- */

function useInfiniteFreeJobs({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): {
  data: any;
  error: any;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => any;
  hasNextPage?: boolean;
} {
  return useInfiniteQuery(
    ["infiniteFreeJobs"],
    ({ pageParam = 1 }) => {
      window.logger(
        "%c[ADMIN][useAdminJobs] getInfiniteFreeJobs FETCHING",
        "background-color: #FF76D2",
        { pageParam },
      );
      return getInfiniteFreeJobs({ page: pageParam });
    },
    {
      enabled,
      onSuccess: (data) => {
        window.logger(
          "%c[ADMIN][useAdminJobs] useInfiniteFreeJobs onSuccess",
          "color: #1976D2",
          data,
        );
      },
      getNextPageParam: (lastPage, allPages) => {
        window.logger(
          "%c[ADMIN][useAdminJobs] getNextPageParam",
          "background-color: #00FF00",
          {
            lastPage,
            allPages,
            meta: lastPage?.meta,
            nextPage: lastPage?.meta?.nextPage,
            isLast: lastPage?.meta?.isLast,
            total: lastPage?.meta?.total,
            page: lastPage?.meta?.page,
          },
        );
        // Return nextPage if it exists and is not null, otherwise undefined
        const nextPage = lastPage?.meta?.nextPage;
        const result = nextPage != null ? nextPage : undefined;
        window.logger(
          "%c[ADMIN][useAdminJobs] getNextPageParam RESULT",
          "background-color: #FFFF00",
          { nextPage, result },
        );
        return result;
      },
    },
  );
}

function useOrgJobs(organizationId: number | null): {
  data: any;
  error: any;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
} {
  window.logger(
    "%c[useAdminJobs] useOrgJobs hook called",
    "color: #FF00FF",
    { organizationId, enabled: organizationId != null && organizationId > 0 },
  );
  return useQuery(
    ["orgJobs", organizationId],
    () => getOrgJobs(organizationId!),
    {
      enabled: organizationId != null && organizationId > 0,
    },
  );
}

function useUpdateAdminJob() {
  const queryClient = useQueryClient();
  return useMutation(updateAdminJob, {
    onSuccess: (data, mutationVariables) => {
      window.logger(
        "%c[ADMIN][useAdminJobs] useUpdateAdminJob onSuccess",
        "color: #1976D2",
        { data, mutationVariables },
      );
      queryClient.invalidateQueries(["infiniteFreeJobs"]);
      queryClient.invalidateQueries(["orgJobs"]);
    },
  });
}

export {
  useInfiniteFreeJobs,
  useOrgJobs,
  useUpdateAdminJob,
};
